import {HttpStatusCode} from 'axios';
import {find, forEach, transform} from 'lodash';
import {Fragment, useCallback, useMemo, useState, useEffect, useContext} from 'react';
import {useBeforeunload} from 'react-beforeunload';
import Div100vh from 'react-div-100vh';
import {useTranslation} from 'react-i18next';
import {RouteComponentProps} from 'react-router';
import {useSocketEvent} from 'socket.io-react-hook';

import {agentActionsApi} from 'api';
import {notifier} from 'components';
import {STATUS_CODES} from 'constants/http';
import {NOT_ALLOWED_ERROR} from 'constants/webrtc';
import {UserContext} from 'context';
import {history} from 'context/history';
import {SocketEvent, LogoutType} from 'enums';
import {useUserSession, useSocketEventCallback, useUserInfoSchema} from 'hooks';
import {
    AsyncRequestStatusName, AsyncRequestTypeName, SessionActivity, AgentParticipant,
    SessionActivityAudioVideoData, SessionStatus, AgentSession, SessionActivityName,
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {userActiveSessionStore} from 'services';
import {ApiError, InviteeParticipantRole, JoinSessionMatchParams} from 'types';
import {platformUtils, httpUtils, logger, sessionUtils, userAuthUtils} from 'utils';

import {InSessionBottomPanel} from './InSessionBottomPanel';
import {InSessionContent} from './InSessionContent';
import {InSessionSidebar} from './InSessionSidebar';
import {InSessionTopPanel} from './InSessionTopPanel';
import {SessionActions} from './SessionActions';
import {SessionEndedView} from './SessionEndedView';
import {
    UserSessionResponsePayload, FromInSessionContextProps, InProgressInSessionAction, InSessionAction, InSessionActionData,
    SidebarView, ResolvedAsyncRequest
} from './types';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const NO_SESSION_ERROR_MSG = 'No active session for user';

export const Conference = ({match}: RouteComponentProps<JoinSessionMatchParams>) => {
    const [t] = useTranslation();
    const [sidebarView, setSidebarView] = useState<SidebarView>(null);
    const [latestResponsePayload, setLatestResponsePayload] = useState<UserSessionResponsePayload>();
    const [responsePayload, setResponsePayload] = useState<UserSessionResponsePayload>();
    const [fullSessionRefreshRequired, setFullSessionRefreshRequired] = useState(true);
    const [rejoinRequired, setRejoinRequired] = useState(true);
    const [resolvedAsyncRequests, setResolvedAsyncRequests] = useState<ResolvedAsyncRequest[]>([]);
    const [inProgressActions, setInProgressActions] = useState<InProgressInSessionAction[]>([]);
    useBeforeunload((event: Event) => event.preventDefault());
    const [localSessionEnded, setLocalSessionEnded] = useState(false);

    const session = useMemo(() => responsePayload?.session, [responsePayload]);
    const {sessionParams, fetchedSessionVersion, audioVideoActivity, isEnded} =
        useUserSession({payload: responsePayload});

    const {socket, auth} = useContext(UserContext);
    const platform = useMemo(() => platformUtils.getPlatformInfo(), []);

    useUserInfoSchema({
        fetchOnInit: true,
        role: InviteeParticipantRole.Client,
        guestEnabled: responsePayload?.settings?.session?.guestEnabled
    });

    const {sendMessage: joinSession} = useSocketEvent(socket, SocketEvent.ActiveSessionJoin);
    const sessionId = useMemo(() => match.params?.sessionId, [match.params.sessionId]);

    const openDefaultPage = useCallback(() => {
        if (auth?.sessionAccessOnly) {
            userAuthUtils.logout(LogoutType.SessionTokenExpired);
        } else {
            history.push('/');
        }
    }, [auth]);

    const onSidebarViewChange = useCallback((newValue: SidebarView) => {
        setSidebarView((value) => value === newValue ? null : newValue);
    }, []);

    const handleInSessionAction = useCallback((promise: Promise<any>, action?: InSessionAction, data?: InSessionActionData) => {
        if (!action) {
            return promise;
        }
        const id = action ? `${action}-${+new Date()}` : null;
        setInProgressActions((prev) => [...prev, {id, action, data}]);
        // TODO: do we need to do anything special with errors?
        return promise.finally(() => setInProgressActions((prev) => prev.filter((item) => item.id !== id)));
    }, []);

    /** Get state handling */

    const getAudioVideoConnection = useCallback((activity: SessionActivity, participantId: string) =>
        (activity?.data as SessionActivityAudioVideoData)?.participants?.[participantId]?.connection, []);

    const handleAudioVideoConnectionErrors = useCallback((payload: UserSessionResponsePayload) => {
        const newSession = payload.session;
        const newAudioVideoActivity = find(newSession.activities, {name: SessionActivityName.AudioVideo});
        if (newAudioVideoActivity) {
            forEach(newSession.participants, (participant) => {
                const connection = getAudioVideoConnection(newAudioVideoActivity, participant.id);
                if (connection?.code) {
                    const previousConnection = getAudioVideoConnection(audioVideoActivity, participant.id);
                    if (connection.status === 'error' && previousConnection?.code !== connection.code) {
                        const message = connection.code === NOT_ALLOWED_ERROR
                            ? t(T_KEYS.MESSAGES.CLIENT_VIDEO_PERMISSIONS_DENIED)
                            : connection.message;
                        notifier.error({title: t(T_KEYS.MESSAGES.CLIENT_VIDEO_FAILED), details: message});
                    }
                }
            });
        }
    }, [t, getAudioVideoConnection, audioVideoActivity]);

    const getAsyncRequest = (newSession: AgentSession, participantId: string, type: AsyncRequestTypeName) =>
        find(sessionUtils.getParticipantById(newSession, participantId)?.asyncRequests, {type});

    const asyncRequestStatusChangedTo = useCallback(
        (newSession: AgentSession, participantId: string, type: AsyncRequestTypeName, status: AsyncRequestStatusName) => {
            const prevSession = session;
            const asyncRequest = getAsyncRequest(newSession, participantId, type);
            const prevAsyncRequest = getAsyncRequest(prevSession, participantId, type);
            return prevAsyncRequest && prevAsyncRequest.status?.name !== status && asyncRequest?.status.name === status;
        }, [session]);

    const cleanResolvedAsyncRequests = useCallback(() => setResolvedAsyncRequests([]), []);

    const getResolvedAsyncRequests = useCallback((payload: UserSessionResponsePayload): ResolvedAsyncRequest[] => {
        const newSession = payload.session;
        return transform(newSession.participants, (resolved, participant) => {
            const participantAsyncRequests = participant?.asyncRequests;
            const participantResolvedAsyncRequests = transform(participantAsyncRequests, (acc, asyncRequest) => {
                const {type} = asyncRequest;
                const changedToSuccess = asyncRequestStatusChangedTo(newSession, participant.id, type, AsyncRequestStatusName.Success);
                const changedToFailed = asyncRequestStatusChangedTo(newSession, participant.id, type, AsyncRequestStatusName.Failed);
                if (changedToSuccess || changedToFailed) {
                    acc.push({participantId: participant.id, request: asyncRequest});
                }
            }, []);
            resolved.push(...participantResolvedAsyncRequests);
        });
    }, [asyncRequestStatusChangedTo]);

    const isOpenedInNewWindow = useCallback((_session: {localParticipant: AgentParticipant}) =>
        _session?.localParticipant?.socket?.id && socket.id && _session?.localParticipant.socket.id !== socket.id,
    [socket]);

    const [isOpenInNewWindow, setIsOpenInNewWindow] = useState(false);

    const failedToJoinSession = useCallback(() => {
        notifier.error({details: t(COMMON_T_KEYS.MESSAGES.ERROR.UNEXPECTED)});
        openDefaultPage();
    }, [t, openDefaultPage]);

    const updatePlatform = useCallback((payload: UserSessionResponsePayload) =>
        agentActionsApi.updateParticipantData(sessionParams, {
            participantId: payload.session.localParticipant?.id,
            updates: {platformInfo: platform}
        }), [platform, sessionParams]);

    const checkOpenedInNewWindow = useCallback((payload: UserSessionResponsePayload) => {
        if (!payload?.session) {
            return;
        }
        if (isOpenedInNewWindow(payload.session)) {
            setIsOpenInNewWindow(true);
        }
    }, [isOpenedInNewWindow]);

    const handleSessionPayload = useCallback((payload: UserSessionResponsePayload) => {
        if (session) {
            checkOpenedInNewWindow(payload);
        } else {
            updatePlatform(payload);
        }
        handleAudioVideoConnectionErrors(payload);
        const newResolvedAsyncRequests = getResolvedAsyncRequests(payload);
        setLatestResponsePayload(payload);
        setResolvedAsyncRequests(newResolvedAsyncRequests);
    }, [session, handleAudioVideoConnectionErrors, getResolvedAsyncRequests, checkOpenedInNewWindow, updatePlatform]);

    const onHttpRequestError = useCallback((error: Error | ApiError) => {
        const apiError = error as ApiError;
        const apiErrorMessage = apiError?.response?.data?.errors?.[0];
        if (apiErrorMessage === NO_SESSION_ERROR_MSG && !isEnded) {
            openDefaultPage();
        }
        if (apiErrorMessage === 'invalid_token') {
            setIsOpenInNewWindow(true);
        }
        if (apiError.response?.status === HttpStatusCode.TooManyRequests) {
            notifier.error({details: t(COMMON_T_KEYS.MESSAGES.ERROR.TOO_MANY_REQUESTS)});
        }
    }, [openDefaultPage, isEnded, t]);

    useSocketEventCallback(socket, SocketEvent.ActiveSessionJoinSuccess, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionJoinSuccess}`);
        setFullSessionRefreshRequired(true);
    }, []));
    useSocketEventCallback(socket, SocketEvent.ActiveSessionJoinError, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionJoinError}`);
        failedToJoinSession();
    }, [failedToJoinSession]));
    useSocketEventCallback(socket, SocketEvent.ActiveSessionDeleted, useCallback((s: AgentSession) => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionDeleted}`, s);
        openDefaultPage();
    }, [openDefaultPage]));

    useSocketEventCallback(socket, SocketEvent.Authenticated, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.Authenticated}`);
        setRejoinRequired(true);
    }, []));

    const requestInitialState = useCallback(() => {
        httpUtils.retryRequest(
            agentActionsApi.getActiveSessionState({...sessionParams}, {settings: true}),
            {notRetryOnStatus: [STATUS_CODES.BAD_REQUEST, STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED]}
        ).then(handleSessionPayload).catch(onHttpRequestError);
    }, [sessionParams, handleSessionPayload, onHttpRequestError]);

    const handleReturnToSession = useCallback(() => {
        setLocalSessionEnded(false);
        setRejoinRequired(true);
    }, []);

    useEffect(() => {
        if (session && rejoinRequired) {
            joinSession({sessionId});
            setRejoinRequired(false);
        }
    }, [joinSession, rejoinRequired, session, sessionId]);

    useEffect(() => {
        if (isOpenInNewWindow) {
            notifier.error({details: t(T_KEYS.MESSAGES.OPENED_ANOTHER_WINDOW)});
            openDefaultPage();
        }
    }, [isOpenInNewWindow, openDefaultPage, t]);

    useEffect(() => {
        if (fullSessionRefreshRequired) {
            setFullSessionRefreshRequired(false);
            requestInitialState();
        }
    }, [fullSessionRefreshRequired, requestInitialState]);

    useEffect(() => {
        if (!latestResponsePayload) {
            return;
        }
        const payload = latestResponsePayload;
        setLatestResponsePayload(null);

        const {session: newSession} = payload;

        logger.debug(
            `Handle session update. Prev version: ${session?.version}, new version: ${payload?.session?.version}`, payload?.session
        );

        if (newSession.version < fetchedSessionVersion) {
            logger.warn('An invalid version sequence occurred. Update ignored');
            return;
        }
        if (newSession.version === fetchedSessionVersion) {
            setResponsePayload((prev) => ({...prev, ...payload, session: {...session, ...newSession}}));
            return;
        }
        if (newSession.status === SessionStatus.SessionEnded && session.status !== SessionStatus.SessionEnded) {
            if (newSession.reassigned) {
                notifier.success({title: t(T_KEYS.MESSAGES.REASSIGNED)});
            }
        }
        setResponsePayload((prev) => ({...prev, ...payload}));
    }, [t, latestResponsePayload, fetchedSessionVersion, session]);

    const handleInSessionActionRequest = useCallback((
        promise: Promise<UserSessionResponsePayload>, action?: InSessionAction, actionData?: InSessionActionData
    ) => handleInSessionAction(promise.catch(onHttpRequestError), action, actionData)
    , [handleInSessionAction, onHttpRequestError]);
    /** Get state handling end */

    const contextProps: FromInSessionContextProps = useMemo(() => ({
        sidebarView, handleInSessionActionRequest, onSidebarViewChange, handleInSessionAction, inProgressActions,
        openDefaultPage, localSessionEnded, setLocalSessionEnded
    }), [
        sidebarView, inProgressActions, handleInSessionAction, handleInSessionActionRequest, onSidebarViewChange,
        openDefaultPage, localSessionEnded
    ]);

    useEffect(() => {
        userActiveSessionStore.mergeValue({
            session: responsePayload?.session,
            settings: responsePayload?.settings
        });
    }, [responsePayload]);

    useEffect(() => () => {
        userActiveSessionStore.setValue(null);
    }, []);

    if (!session) {
        return null; // or loading?
    }

    return (
        <SessionActions
            contextProps={contextProps}
            payload={responsePayload}
            resolvedAsyncRequests={resolvedAsyncRequests}
            cleanResolvedAsyncRequests={cleanResolvedAsyncRequests}
            handleSessionPayload={handleSessionPayload}
            isOpenedInNewWindow={isOpenInNewWindow}
        >
            <Div100vh style={{width: '100vw'}} className='tw-flex'>
                {(isEnded || localSessionEnded) ?
                    <SessionEndedView onReturnToSession={handleReturnToSession} /> :
                    <Fragment>
                        <div className='tw-relative tw-flex tw-flex-col tw-flex-1 tw-bg-layout tw-max-w-full'>
                            <InSessionTopPanel />
                            <InSessionContent />
                            <InSessionBottomPanel />
                        </div>
                        <InSessionSidebar />
                    </Fragment>}
            </Div100vh>
        </SessionActions>
    );
};
